import { x25519 } from '@noble/curves/ed25519.js';
import { chacha20poly1305 } from '@noble/ciphers/chacha.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes, utf8ToBytes, concatBytes, randomBytes } from '@noble/hashes/utils.js';
import { isHex } from './crypto.js';

/**
 * Hidden withdrawal addresses. The log keeps only a ciphertext for the
 * sequencer and a commitment H(salt ‖ address). The user keeps the salt and
 * can prove where a withdrawal went; nobody else can read it from the log.
 *
 * enc = ephemeral x25519 pubkey (32) ‖ ChaCha20-Poly1305(salt (16) ‖ address).
 * The key is fresh per message (ephemeral ECDH), so a fixed nonce is safe.
 */
const SALT_BYTES = 16;
const NONCE = new Uint8Array(12);
export const MAX_ADDRESS_BYTES = 700;
/** Byte bounds of `enc`: header + tag + salt + 1..700 address bytes. */
export const ENC_MIN_BYTES = 32 + 16 + SALT_BYTES + 1;
export const ENC_MAX_BYTES = 32 + 16 + SALT_BYTES + MAX_ADDRESS_BYTES;

/**
 * Commitment to where a withdrawal goes, bound to the account that asked for
 * it: a sealed address copied into someone else's request does not open.
 */
export function withdrawalCommit(saltHex: string, address: string, user: string): string {
  return bytesToHex(sha256(concatBytes(utf8ToBytes('DAZZLE/v3/withdraw-commit\n'), hexToBytes(user), hexToBytes(saltHex), utf8ToBytes(address))));
}

function streamKey(shared: Uint8Array, ephPub: Uint8Array, recipient: Uint8Array) {
  return hkdf(sha256, shared, concatBytes(ephPub, recipient), utf8ToBytes('DAZZLE/v1/withdraw-enc'), 32);
}

/** Client side: seal a withdrawal address to the sequencer's encryption key, for account `user`. */
export function sealAddress(encKeyHex: string, address: string, user: string, saltHex = bytesToHex(randomBytes(SALT_BYTES))) {
  const addr = utf8ToBytes(address);
  if (addr.length === 0 || addr.length > MAX_ADDRESS_BYTES) throw new Error('address length');
  const recipient = hexToBytes(encKeyHex);
  const eph = x25519.keygen();
  const key = streamKey(x25519.getSharedSecret(eph.secretKey, recipient), eph.publicKey, recipient);
  // The account is authenticated data: the ciphertext only opens for the request of that account.
  const ct = chacha20poly1305(key, NONCE, hexToBytes(user)).encrypt(concatBytes(hexToBytes(saltHex), addr));
  return { enc: bytesToHex(concatBytes(eph.publicKey, ct)), commit: withdrawalCommit(saltHex, address, user), salt: saltHex };
}

/** Sequencer side: recover the address of `user`'s withdrawal, or null if it does not decrypt or match the commitment. */
export function openAddress(encSecret: Uint8Array, encHex: string, commit: string, user: string): string | null {
  try {
    if (!isHex(encHex) || encHex.length / 2 < ENC_MIN_BYTES || encHex.length / 2 > ENC_MAX_BYTES || !isHex(user, 32)) return null;
    const enc = hexToBytes(encHex);
    const ephPub = enc.subarray(0, 32);
    const key = streamKey(x25519.getSharedSecret(encSecret, ephPub), ephPub, x25519.getPublicKey(encSecret));
    const pt = chacha20poly1305(key, NONCE, hexToBytes(user)).decrypt(enc.subarray(32));
    const salt = bytesToHex(pt.subarray(0, SALT_BYTES));
    const address = new TextDecoder('utf-8', { fatal: true }).decode(pt.subarray(SALT_BYTES));
    return withdrawalCommit(salt, address, user) === commit ? address : null;
  } catch {
    return null;
  }
}
